import { ART_SCHEMA, ASPECT_RATIOS, ID_PREFIX, formatRatios, newId, normalizeTextStyle } from './schema';
import { textHeight } from './textLayout';
import { readableInk } from './colors';
import { SQUARE_SHAPES } from './shapes';

// New documents, pages and layers with sensible defaults. Positions default to the page
// center; every layer gets a fresh `sly-` id.
export function createPage({ background = { type: 'solid', color: '#ffffff' }, layers = [], name = null } = {}) {
  return { id: newId(ID_PREFIX.page), name, background, layers };
}

export function createDocument({ format = 'instagram::post', aspectRatio, background, layers } = {}) {
  const ratio = formatRatios(format).includes(aspectRatio) ? aspectRatio : formatRatios(format)[0];
  const size = ASPECT_RATIOS[ratio];
  return { schema: ART_SCHEMA, format, aspect_ratio: ratio, width: size.width, height: size.height, pages: [createPage({ background, layers })] };
}

const base = (type, { x = 0, y = 0, width = 100, height = 100, rotation = 0, opacity = 1, name = null, binding = null } = {}) => ({
  id: newId(ID_PREFIX.layer), type, name, binding, x, y, width, height, rotation, opacity, visible: true, locked: false,
});
const centered = (size, width, height) => ({ x: Math.round((size.width - width) / 2), y: Math.round((size.height - height) / 2) });

// Heading, subheading and body follow the brand fonts when there are any.
export const TEXT_PRESETS = {
  heading: { font_size: 96, font_weight: 700, line_height: 1.08, role: 'heading', binding: 'headline' },
  subheading: { font_size: 56, font_weight: 600, line_height: 1.15, role: 'subheading', binding: 'subheadline' },
  body: { font_size: 36, font_weight: 400, line_height: 1.35, role: 'body', binding: 'body' },
};

// A new text in a preset, in the brand kit's style for its role when `fonts` (a kit's text
// styles) has one: its face and weight and, 2.10.0, its size, italic, case and spacing.
export function createTextLayer(size, { preset = 'body', text, fonts, color, style = {}, width, ...placement } = {}) {
  const { role, binding, ...presetStyle } = TEXT_PRESETS[preset] || TEXT_PRESETS.body;
  const font = fonts ? fonts[role] || (role === 'subheading' ? fonts.heading : null) : null;
  const kit = font ? { font_family: font.family, font_weight: font.weight || presetStyle.font_weight, ...(Number.isFinite(font.size) ? { font_size: font.size } : {}),
    ...(typeof font.italic === 'boolean' ? { italic: font.italic } : {}), ...(['none', 'uppercase'].includes(font.transform) ? { transform: font.transform } : {}),
    ...(Number.isFinite(font.line_height) ? { line_height: font.line_height } : {}), ...(Number.isFinite(font.letter_spacing) ? { letter_spacing: font.letter_spacing } : {}) } : {};
  const normalized = normalizeTextStyle({ align: 'center', color: color || '#111111', ...presetStyle, ...kit, ...style });
  const boxWidth = width || Math.round(size.width * (preset === 'body' ? 0.7 : 0.8));
  const layer = { ...base('text', { width: boxWidth, binding: placement.binding === undefined ? binding : placement.binding }), text: text || '', style: normalized };
  layer.height = textHeight(layer);
  const position = centered(size, layer.width, layer.height);
  return { ...layer, x: placement.x ?? position.x, y: placement.y ?? position.y, name: placement.name || null };
}

export function createShapeLayer(size, shape, { fill, stroke, cornerRadius = 0, width, height, ...placement } = {}) {
  const square = SQUARE_SHAPES.includes(shape);
  const w = width || (shape === 'line' ? Math.round(size.width * 0.5) : shape === 'arrow' ? 320 : 360);
  const h = height || (shape === 'line' ? 24 : shape === 'arrow' ? 140 : square ? w : 240);
  const layer = { ...base('shape', { width: w, height: h, ...placement }), shape,
    fill: shape === 'line' ? null : fill || { type: 'solid', color: '#1f4d3a' },
    stroke: shape === 'line' ? stroke || { color: '#111111', width: 8 } : stroke || null,
    corner_radius: shape === 'rect' ? cornerRadius : 0 };
  return { ...layer, ...centered(size, w, h), ...pickPosition(placement) };
}

// An image fitted inside 70% of the page, keeping its own proportions.
export function createImageLayer(size, source, { fit = 'cover', ...placement } = {}) {
  const ratio = source.width / source.height || 1;
  const maxW = size.width * 0.7;
  const maxH = size.height * 0.7;
  const width = Math.round(ratio >= maxW / maxH ? maxW : maxH * ratio);
  const height = Math.round(width / ratio);
  const layer = { ...base('image', { width, height, binding: 'photo', ...placement }), source, fit, focus_x: 0.5, focus_y: 0.5, zoom: 1, corner_radius: 0,
    flip_x: false, flip_y: false, adjust: { brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false } };
  return { ...layer, ...centered(size, width, height), ...pickPosition(placement) };
}

// A video (2.13.0) placed like an image (inside 70% of the page, its own proportions),
// playing `trim` of its file from the page's start, with its sound, in a rectangle.
export function createVideoLayer(size, source, { trim = { start: 0, end: source.duration }, muted = false, ...placement } = {}) {
  const image = createImageLayer(size, source, { binding: null, ...placement });
  return { ...image, type: 'video', trim, at: 0, muted, volume: 1, mask: 'rect' };
}

export function createIconLayer(size, icon, { color = '#111111', strokeWidth = 2, side = 200, ...placement } = {}) {
  const layer = { ...base('icon', { width: side, height: side, ...placement }), icon, color, stroke_width: strokeWidth };
  return { ...layer, ...centered(size, side, side), ...pickPosition(placement) };
}

const pickPosition = ({ x, y }) => ({ ...(x !== undefined ? { x } : {}), ...(y !== undefined ? { y } : {}) });

// A text color that reads on the page background, for text added from the panel.
export const inkFor = page => readableInk(page?.background?.type === 'linear' ? page.background.stops[0].color : page?.background?.color || '#ffffff');
